/**
 * Public landing page.
 *
 * Loads the saleable catalogue and renders one card per SKU. Card click →
 * /buy/:slug.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useLang } from '@/i18n/useLang';
import { storefrontApi } from '@/api/storefront';
import type { SkuEntry } from '@/api/storefront';
import { StorefrontHeader } from '@/components/storefront/StorefrontHeader';

export function LandingPage() {
  const { lang } = useLang();
  const cat = useQuery({
    queryKey: ['storefront', 'catalogue'],
    queryFn: () => storefrontApi.catalogue(),
    staleTime: 60_000,
  });

  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  return (
    <>
      <StorefrontHeader />
      <main className="mx-auto max-w-screen-xl px-4 py-10 lg:px-6 lg:py-14">
        <section className="mb-10 text-center">
          <h1 className="text-3xl font-bold text-saffron-100 lg:text-4xl">
            {t('पारंपरिक शास्त्रों पर आधारित ज्योतिषीय रिपोर्ट',
               'Vedic Astrology Reports — Rooted in Classical Texts')}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-stone-400 lg:text-base">
            {t(
              'सत्यापित खगोलीय गणना, श्लोक-संदर्भित कार्यप्रणाली, हिन्दी अथवा अंग्रेज़ी में सुंदर PDF रिपोर्ट — सीधे आपके ईमेल पर।',
              'Verified Swiss-Ephemeris calculations, śloka-cited methodology, beautifully typeset PDFs in Hindi or English — delivered straight to your inbox.',
            )}
          </p>
        </section>

        {cat.isLoading && (
          <div className="py-10 text-center text-sm text-stone-500">
            {t('लोड हो रहा है…', 'Loading…')}
          </div>
        )}
        {cat.isError && (
          <div className="py-10 text-center text-sm text-rose-300">
            {t('कैटलॉग लोड नहीं हो सका।', 'Could not load the catalogue.')}
          </div>
        )}

        {cat.data && (
          <>
            {!cat.data.razorpay_configured && (
              <div className="mb-6 rounded-xl bg-amber-950/50 px-4 py-3 text-sm text-amber-200 ring-1 ring-amber-900">
                {t(
                  'अभी ऑनलाइन भुगतान कॉन्फ़िगर नहीं है। केवल कूपन-कोड वाली निःशुल्क रिपोर्ट उपलब्ध हैं।',
                  'Online payment is not configured yet. Only free coupon-based reports are available right now.',
                )}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cat.data.skus.map((sku) => (
                <SkuCard key={sku.slug} sku={sku} />
              ))}
            </div>
          </>
        )}

        <footer className="mt-16 border-t border-stone-800 pt-6 text-center text-xs text-stone-500">
          {t(
            'सहायता या प्रश्न के लिए: sachin.jpr@gmail.com',
            'Support / questions: sachin.jpr@gmail.com',
          )}
        </footer>
      </main>
    </>
  );
}

function SkuCard({ sku }: { sku: SkuEntry }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const badgeLabel: Record<string, { hi: string; en: string }> = {
    flagship: { hi: 'फ़्लैगशिप', en: 'Flagship' },
    new: { hi: 'नया', en: 'New' },
  };
  return (
    <Link
      to={`/buy/${sku.slug}`}
      className="group flex flex-col gap-3 rounded-2xl border border-stone-800 bg-stone-900/60 p-5 ring-1 ring-stone-800 transition hover:border-saffron-700/60 hover:bg-stone-900/90 hover:ring-saffron-700/40"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-2xl">{sku.emoji}</span>
        {sku.badge && (
          <span className="rounded-full bg-saffron-700/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-saffron-200">
            {t(badgeLabel[sku.badge]?.hi ?? sku.badge, badgeLabel[sku.badge]?.en ?? sku.badge)}
          </span>
        )}
      </div>
      <h3 className="text-base font-semibold text-stone-100 group-hover:text-saffron-200">
        {t(sku.title_hi, sku.title_en)}
      </h3>
      <p className="flex-1 text-xs leading-relaxed text-stone-400">
        {t(sku.blurb_hi, sku.blurb_en)}
      </p>
      <div className="mt-2 flex items-center justify-between border-t border-stone-800 pt-3">
        <span className={`text-lg font-bold ${sku.price_paise === 0 ? 'text-emerald-300' : 'text-saffron-100'}`}>
          {sku.price_inr_display}
        </span>
        <span className="text-xs text-saffron-300 group-hover:underline">
          {sku.price_paise === 0
            ? t('प्राप्त करें →', 'Get it →')
            : t('ख़रीदें →', 'Buy now →')}
        </span>
      </div>
    </Link>
  );
}
